import { useState, useEffect, useContext } from 'react'
import AuthService from '../services/auth.service'
import ThemeContext from '../contexts/themeContext'
import { 
  validateKpiData, 
  getDefaultKpiData, 
  getTranslatedLabels, 
  getColorsForKpi,
  processLogData 
} from '../utils/chartUtils'
import { 
  getRadialScaleConfig, 
  getLogChartOptions,
  RADIAL_CHART_OPTIONS,
} from '../constants/chartConstants'

/**
 * Hook personnalisé pour gérer les données des graphiques
 */
export const useChartData = (kpiDocument, name, stacked = true) => {
  const [cyberchartState, setCyberchart] = useState({})
  const [chartLogData, setChartLogData] = useState({
    labels: 'null',
    datasets: [],
  })
  const [chartLogOptions, setChartLogOptions] = useState({})
  
  const currentUser = AuthService.getCurrentUser()
  const { theme } = useContext(ThemeContext)

  // Initialisation des options de graphique de logs
  useEffect(() => {
    const options = getLogChartOptions(theme, true)
    setChartLogOptions(options)
  }, [theme])

  // Mise à jour des graphiques quand les données changent
  useEffect(() => {
    const sname = name === 'log_full' ? 'log' : name
    if (kpiDocument[sname]) {
      const chart = setChart(sname)
      setCyberchart(chart)
    }
  }, [kpiDocument, theme, name])

  // Mise à jour des données de logs
  const updateLogChartData = (stackedArg = stacked) => {
    if (!validateKpiData(kpiDocument, 'log')) {
      return
    }

    const { datasets, labels } = processLogData(kpiDocument, stackedArg, currentUser)
    setChartLogData({ datasets, labels })
  }

  // Mise à jour des données de logs quand kpiDocument, name ou stacked change
  useEffect(() => {
    if (name === 'log' || name === 'log_full') {
      updateLogChartData(stacked)
    }
  }, [kpiDocument, name, stacked])

  // Création des configurations de graphiques par type
  const setChart = (chartName) => {
    const cybercharts = {}

    switch (chartName) {
      case 'er_supervise':
        return createErSuperviseChart(cybercharts)
      case 'mitre_attack':
        return createMitreAttackChart(cybercharts)
      case 'perimetre_supervise':
        return createPerimetreSuperviseChart(cybercharts)
      case 'log':
      case 'log_full':
        return createLogChart(cybercharts)
      default:
        return cybercharts
    }
  }

  // Création du graphique er_supervise
  const createErSuperviseChart = (cybercharts) => {
    if (!validateKpiData(kpiDocument, 'er_supervise')) {
      kpiDocument.er_supervise = getDefaultKpiData('er_supervise')
    }

    const labels = getTranslatedLabels(kpiDocument.er_supervise.result.fields, 'er_supervise')
    const colors = getColorsForKpi(kpiDocument.er_supervise.result.fields, 'er_supervise')

    cybercharts.er_supervise = {
      data: {
        labels,
        datasets: [{
          data: kpiDocument.er_supervise.result.rows,
          backgroundColor: colors,
        }],
      },
      options: {
        ...RADIAL_CHART_OPTIONS,
        plugins: {
          ...RADIAL_CHART_OPTIONS.plugins,
          legend: {
            ...RADIAL_CHART_OPTIONS.plugins.legend,
            labels: {
              ...RADIAL_CHART_OPTIONS.plugins.legend.labels,
              color: theme === 'dark' ? '#fff' : '#666'
            },
          },
        },
        scales: {
          r: getRadialScaleConfig(theme, false, false, false),
        },
      },
    }

    return cybercharts
  }

  // Création du graphique mitre_attack
  const createMitreAttackChart = (cybercharts) => {
    if (!validateKpiData(kpiDocument, 'mitre_attack')) {
      kpiDocument.mitre_attack = getDefaultKpiData('mitre_attack')
    }

    const labels = kpiDocument.mitre_attack.result.fields
    const colors = getColorsForKpi([], 'mitre_attack')

    cybercharts.mitre_attack = {
      data: {
        labels,
        datasets: [
          {
            label: 'En production',
            data: kpiDocument.mitre_attack.result.rows.map((row) => row[0]),
            backgroundColor: colors[0],
          },
          {
            label: 'Hors production',
            data: kpiDocument.mitre_attack.result.rows.map((row) => row[1]),
            backgroundColor: colors[1],
          },
        ],
      },
      options: {
        ...RADIAL_CHART_OPTIONS,
        plugins: {
          ...RADIAL_CHART_OPTIONS.plugins,
          legend: {
            ...RADIAL_CHART_OPTIONS.plugins.legend,
            labels: {
              ...RADIAL_CHART_OPTIONS.plugins.legend.labels,
              color: theme === 'dark' ? '#fff' : '#666'
            },
          },
        },
        scales: {
          r: getRadialScaleConfig(theme, true, true, true),
        },
      },
    }

    return cybercharts
  }

  // Création du graphique perimetre_supervise
  const createPerimetreSuperviseChart = (cybercharts) => {
    if (!validateKpiData(kpiDocument, 'perimetre_supervise')) {
      kpiDocument.perimetre_supervise = getDefaultKpiData('perimetre_supervise')
    }

    const labels = kpiDocument.perimetre_supervise.result.fields
    const colors = getColorsForKpi(kpiDocument.perimetre_supervise.result.fields, 'perimetre_supervise')

    cybercharts.perimetre_supervise = {
      data: {
        labels,
        datasets: [{
          data: kpiDocument.perimetre_supervise.result.rows,
          backgroundColor: colors,
        }],
      },
      options: {
        ...RADIAL_CHART_OPTIONS,
        plugins: {
          ...RADIAL_CHART_OPTIONS.plugins,
          legend: {
            ...RADIAL_CHART_OPTIONS.plugins.legend,
            labels: {
              ...RADIAL_CHART_OPTIONS.plugins.legend.labels,
              color: theme === 'dark' ? '#fff' : '#666'
            },
          },
        },
        scales: {
          r: getRadialScaleConfig(theme, false, false, false),
        },
      },
    }

    return cybercharts
  }

  // Création du graphique de logs
  const createLogChart = (cybercharts) => {
    if (!validateKpiData(kpiDocument, 'log')) {
      kpiDocument.log = getDefaultKpiData('log')
    }

    // Ne pas appeler updateLogChartData ici pour éviter les boucles infinies
    // Les données seront mises à jour via useEffect séparé
    
    cybercharts.log_full = {
      data: chartLogData,
      options: chartLogOptions,
    }

    return cybercharts
  }

  return {
    cyberchartState,
    chartLogData,
    chartLogOptions,
    updateLogChartData,
    setChartLogOptions
  }
}
